import React, { useEffect, useState } from 'react';
import './circle.css';
import { useNavigate } from 'react-router-dom';
import adminService from '../../../services/adminService';
import '../components/AdminPanel.css';
import './dashboard.css';
import { 
  Users, 
  Building, 
  Contact, 
  FileText, 
  Send, 
  CheckCircle,
  Calendar,
  UserCheck,
  UserX,
  Briefcase,
  Target,
  BarChart3
} from 'lucide-react';

type DateRange = 'today' | 'this_week' | 'this_month' | 'last_6_months' | 'this_year' | 'all';

interface ApiDashboardData {
  date_range: {
    start_date: string;
    end_date: string;
    duration_days: number;
    filter_used: string;
  };
  cc_users: {
    total: number;
    role_not_6: { active: number; inactive: number };
    role_6: { active: number; inactive: number };
  };
  cc_b2b: { companies: number; contacts: number };
  cc_b2c: { companies: number; contacts: number };
  cc_fdc_master: { total: number; not_sent: number; sent_completed: number };
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<DateRange>('this_week');
  const [data, setData] = useState<ApiDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async (time: DateRange) => {
    try {
      setLoading(true);
      const response = await adminService.count(time);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData(selectedDate);
  }, [selectedDate]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="error-container">
        <p>No data available. Please try again.</p>
      </div>
    );
  }

  // Helper function to format dates
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Calculate derived statistics
  const totalActiveUsers = data.cc_users.role_not_6.active + data.cc_users.role_6.active;
  const totalInactiveUsers = data.cc_users.role_not_6.inactive + data.cc_users.role_6.inactive;
  const totalContacts = data.cc_b2b.contacts + data.cc_b2c.contacts;
  const totalCompanies = data.cc_b2b.companies + data.cc_b2c.companies;
  const sentPercentage = data.cc_fdc_master.total > 0 
    ? Math.round((data.cc_fdc_master.sent_completed / data.cc_fdc_master.total) * 100)
    : 0;

  return (
    <div className="dashboard-container">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Dashboard Overview</h1>
          <div className="date-badge">
            <Calendar size={16} />
            <span>
              {formatDate(data.date_range.start_date)} - {formatDate(data.date_range.end_date)}
            </span>
            <span className="badge">• {data.date_range.duration_days} days</span>
          </div>
        </div>

        <div className="header-controls">
          <div className="filter-select-wrapper">
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value as DateRange)}
              className="filter-select"
            >
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="last_6_months">Last 6 Months</option>
              <option value="this_year">This Year</option>
              <option value="all">All Time</option>
            </select>
            <div className="filter-icon">▼</div>
          </div>
        </div>
      </div>

      {/* Main Dashboard Grid */}
      <div className="dashboard-grid">
        
        {/* Row 1: User Statistics */}
        <div className="section-header">
          <h2><Users size={20} /> User Management</h2>
          <div className="section-subtitle">Total Users: {data.cc_users.total}</div>
        </div>
        
        <div className="user-stats-grid">
          {/* Total Users Card */}
          <div className="stat-card primary" style={{cursor:"pointer"}} onClick={() => navigate("/admin/users-list")}>
            <div className="card-header">
              <h3>Total Users</h3>
              <Users size={20} />
            </div>
            <div className="card-value">{data.cc_users.total}</div>
            <div className="card-details">
              <div className="detail-row">
                <span className="detail-label">Active Users</span>
                <span className="detail-value success">{totalActiveUsers}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Inactive Users</span>
                <span className="detail-value muted">{totalInactiveUsers}</span>
              </div>
            </div>
          </div>

          {/* Internal Users Card */}
          <div className="stat-card secondary">
            <div className="card-header">
              <h3>Internal Users</h3>
              <UserCheck size={20} />
            </div>
            <div className="card-value">{data.cc_users.role_not_6.active + data.cc_users.role_not_6.inactive}</div>
            <div className="card-details">
              <div className="detail-row">
                <span className="detail-label">Active</span>
                <span className="detail-value success">{data.cc_users.role_not_6.active}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Inactive</span>
                <span className="detail-value muted">{data.cc_users.role_not_6.inactive}</span>
              </div>
            </div>
          </div>

          {/* DCCP Users Card */}
          <div className="stat-card accent">
            <div className="card-header">
              <h3>DCCP Users</h3>
              <Briefcase size={20} />
            </div>
            <div className="card-value">{data.cc_users.role_6.active + data.cc_users.role_6.inactive}</div>
            <div className="card-details">
              <div className="detail-row">
                <span className="detail-label">Active</span>
                <span className="detail-value success">{data.cc_users.role_6.active}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Inactive</span>
                <span className="detail-value muted">{data.cc_users.role_6.inactive}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Company Statistics */}
        <div className="section-header">
          <h2><Building size={20} /> RDC Database</h2>
          <div className="section-subtitle">Total Companies: {totalCompanies}</div>
        </div>
        
        <div className="company-stats-grid">
          {/* B2B Companies */}
          <div className="stat-card success">
            <div className="card-header">
              <h3>B2B Companies</h3>
              <Building size={20} />
            </div>
            <div className="card-value">{data.cc_b2b.companies}</div>
            <div className="card-details">
              <div className="detail-row">
                <span className="detail-label">Total Contacts</span>
                <span className="detail-value highlight">{data.cc_b2b.contacts}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Contacts/Company</span>
                <span className="detail-value">
                  {data.cc_b2b.companies > 0 
                    ? (data.cc_b2b.contacts / data.cc_b2b.companies).toFixed(1)
                    : '0.0'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* B2C Companies */}
          <div className="stat-card warning">
            <div className="card-header">
              <h3>B2C Companies</h3>
              <Contact size={20} />
            </div>
            <div className="card-value">{data.cc_b2c.companies}</div>
            <div className="card-details">
              <div className="detail-row">
                <span className="detail-label">Total Contacts</span>
                <span className="detail-value highlight">{data.cc_b2c.contacts}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Contacts/Company</span>
                <span className="detail-value">
                  {data.cc_b2c.companies > 0 
                    ? (data.cc_b2c.contacts / data.cc_b2c.companies).toFixed(1)
                    : '0.0'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* Contacts Summary */}
          <div className="stat-card info">
            <div className="card-header">
              <h3>Contacts Summary</h3>
              <Target size={20} />
            </div>
            <div className="card-value">{totalContacts}</div>
            <div className="card-details">
              <div className="detail-row">
                <span className="detail-label">B2B Contacts</span>
                <span className="detail-value">{data.cc_b2b.contacts}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">B2C Contacts</span>
                <span className="detail-value">{data.cc_b2c.contacts}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Total Companies</span>
                <span className="detail-value total">{totalCompanies}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Row 3: FDC Projects */}
        <div className="section-header">
          <h2><FileText size={20} /> FDC Project Management</h2>
          <div className="section-subtitle">Total Projects: {data.cc_fdc_master.total}</div>
        </div>
        
        <div className="fdc-grid">
          {/* FDC Projects Card */}
          <div className="stat-card primary">
            <div className="card-header">
              <h3>Total FDC Projects</h3>
              <FileText size={20} />
            </div>
            <div className="card-value">{data.cc_fdc_master.total}</div>
            <div className="progress-container">
              <div className="progress-header">
                <span>Completion Rate</span>
                <span className="progress-percentage">{sentPercentage}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${sentPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          {/* Project Status Cards */}
          <div className="status-cards-grid">
            <div className="status-card pending">
              <div className="status-header">
                <Send size={18} />
                <h4>Not Sent</h4>
              </div>
              <div className="status-value">{data.cc_fdc_master.not_sent}</div>
              <div className="status-percentage">
                {data.cc_fdc_master.total > 0 
                  ? Math.round((data.cc_fdc_master.not_sent / data.cc_fdc_master.total) * 100)
                  : 0
                }%
              </div>
            </div>

            <div className="status-card completed">
              <div className="status-header">
                <CheckCircle size={18} />
                <h4>Completed</h4>
              </div>
              <div className="status-value">{data.cc_fdc_master.sent_completed}</div>
              <div className="status-percentage">
                {data.cc_fdc_master.total > 0 
                  ? Math.round((data.cc_fdc_master.sent_completed / data.cc_fdc_master.total) * 100)
                  : 0
                }%
              </div>
            </div>
          </div>
        </div>       
      </div>
    </div>
  );
};

export default Dashboard;